import { m } from "@estate-platform/i18n/messages";
import { createFileRoute, getRouteApi, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { AuthPending } from "../auth/auth-layout.tsx";
import { destinationAfterSignIn } from "../auth/backup-prompt.ts";
import { VerifyPage } from "../auth/verify-page.tsx";

import type { VerifyStep } from "../auth/verify-page.tsx";
import type { DataLayer } from "../data/data-layer.tsx";
import type { ReactElement } from "react";

const route = getRouteApi("/sign-in/verify");

const Verify = (): ReactElement => <VerifyPage step={route.useLoaderData()} />;

const followLink = async (data: DataLayer, tokenHash: string): Promise<VerifyStep> => {
  if ((await data.auth.verifyLink(tokenHash)) === "expired") {
    return { kind: "expired" };
  }
  throw redirect({ to: "/sign-in/verify", replace: true });
};

export const Route = createFileRoute("/sign-in/verify")({
  validateSearch: z.object({
    token_hash: z.string().min(1).max(512).optional().catch(undefined),
    type: z.literal("email").optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ tokenHash: search.token_hash }),
  loader: async ({ context: { data }, deps }): Promise<VerifyStep> => {
    if (deps.tokenHash !== undefined) {
      return followLink(data, deps.tokenHash);
    }
    const user = await data.auth.currentUser();
    if (user === null) {
      return { kind: "expired" };
    }
    if (user.isComplete) {
      throw redirect({ to: await destinationAfterSignIn(data), replace: true });
    }
    const [passkeys, hasCode] = await Promise.all([data.factors.passkeys(), data.auth.hasCode()]);
    return { kind: "secondFactor", hasPasskey: passkeys.length > 0, hasCode };
  },
  pendingComponent: () => <AuthPending title={m.verify_title()} />,
  component: Verify,
});
